import { Trash2, Copy, Server, HardDrive, Cpu, Activity, Settings, Search, X, ChevronDown, ChevronUp, LayoutList, LayoutGrid, Network } from 'lucide-react';
import { Card, CardContent, Button, Input, ValidatedInput, NodeTypeBadge, Badge } from '../../ui';
import { useConfigurator } from '../../../hooks/useConfigurator';
import { NodeType, ComputeRole, DatacenterNode, GpuSpecs, NetworkSegment } from '../../../types/configurator';
import { HARDWARE_CATALOG, HardwareModel } from '../../../data/scenarios';
import { GpuSelector } from './GpuSelector';
import { DraggableNodeCard } from './DraggableNodeCard';
import { RackView, ContainerView } from '../../rack';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  validateNodeName,
  validateHostname,
  validateIPv4,
  validateUniqueIP,
  validateUniqueHostname,
} from '../../../utils/validation';
import { clsx } from 'clsx';
import { useState, useCallback, useMemo } from 'react';

const HARDWARE_OPTIONS: { value: HardwareModel | 'custom'; label: string; category: string }[] = [
  // SBC / Edge
  { value: 'raspberry-pi-4b', label: 'Raspberry Pi 4B (4GB)', category: 'SBC' },
  { value: 'raspberry-pi-cm4', label: 'Raspberry Pi CM4 (8GB)', category: 'SBC' },
  { value: 'beaglebone', label: 'BeagleBone Black', category: 'SBC' },
  // Mini PC
  { value: 'intel-n100', label: 'Intel N100 Mini PC', category: 'Mini PC' },
  { value: 'intel-n305', label: 'Intel N305 Edge PC', category: 'Mini PC' },
  { value: 'intel-nuc-13', label: 'Intel NUC 13 Pro', category: 'Mini PC' },
  // Workstations
  { value: 'amd-ryzen-7', label: 'AMD Ryzen 7 7735HS', category: 'Workstation' },
  { value: 'xeon-w1390', label: 'Intel Xeon W-1390', category: 'Workstation' },
  { value: 'epyc-7313p', label: 'AMD EPYC 7313P', category: 'Workstation' },
  // HP Servers
  { value: 'hp-dl360-gen10', label: 'HP ProLiant DL360 Gen10 (1U)', category: 'HP Server' },
  { value: 'hp-dl380-gen10', label: 'HP ProLiant DL380 Gen10 (2U)', category: 'HP Server' },
  { value: 'hp-dl380-gen10-gpu', label: 'HP ProLiant DL380 Gen10+ GPU (2U)', category: 'HP Server' },
  { value: 'hp-ml350-gen10', label: 'HP ProLiant ML350 Gen10 (4U)', category: 'HP Server' },
  // Dell Servers
  { value: 'dell-r350', label: 'Dell PowerEdge R350 (1U)', category: 'Dell Server' },
  { value: 'dell-r650', label: 'Dell PowerEdge R650 (1U)', category: 'Dell Server' },
  { value: 'dell-r750', label: 'Dell PowerEdge R750 (2U)', category: 'Dell Server' },
  { value: 'dell-r750xa', label: 'Dell PowerEdge R750xa GPU (2U)', category: 'Dell Server' },
  { value: 'dell-r760', label: 'Dell PowerEdge R760 (2U)', category: 'Dell Server' },
  { value: 'dell-r760xa', label: 'Dell PowerEdge R760xa GPU (2U)', category: 'Dell Server' },
  { value: 'dell-t350', label: 'Dell PowerEdge T350 Tower (4U)', category: 'Dell Server' },
  { value: 'dell-t550', label: 'Dell PowerEdge T550 Tower (5U)', category: 'Dell Server' },
  // Supermicro Servers
  { value: 'supermicro-e100', label: 'Supermicro E100-12T (Edge)', category: 'Supermicro' },
  { value: 'supermicro-e300', label: 'Supermicro E300-9D (Xeon D)', category: 'Supermicro' },
  { value: 'supermicro-5019s', label: 'Supermicro 5019S-MN4 (1U)', category: 'Supermicro' },
  { value: 'supermicro-5019p', label: 'Supermicro 5019P-MTR (1U)', category: 'Supermicro' },
  { value: 'supermicro-6029p', label: 'Supermicro 6029P-TRT (2U)', category: 'Supermicro' },
  { value: 'supermicro-4029gp', label: 'Supermicro 4029GP GPU (4U)', category: 'Supermicro' },
  { value: 'supermicro-2029u', label: 'Supermicro 2029U (2U HDD)', category: 'Supermicro' },
  { value: 'supermicro-workstation', label: 'Supermicro Workstation 7049A', category: 'Supermicro' },
  // Lenovo ThinkSystem Servers
  { value: 'lenovo-se350', label: 'Lenovo SE350 (Edge)', category: 'Lenovo' },
  { value: 'lenovo-sr250', label: 'Lenovo SR250 V2 (1U)', category: 'Lenovo' },
  { value: 'lenovo-sr630', label: 'Lenovo SR630 V3 (1U)', category: 'Lenovo' },
  { value: 'lenovo-sr650', label: 'Lenovo SR650 V3 (2U)', category: 'Lenovo' },
  { value: 'lenovo-sr670', label: 'Lenovo SR670 V2 GPU (2U)', category: 'Lenovo' },
  { value: 'lenovo-sr850', label: 'Lenovo SR850 V3 (2U 4-Socket)', category: 'Lenovo' },
  { value: 'lenovo-sd530', label: 'Lenovo SD530 (High Density)', category: 'Lenovo' },
  { value: 'lenovo-st250', label: 'Lenovo ST250 V2 Tower', category: 'Lenovo' },
  { value: 'lenovo-st650', label: 'Lenovo ST650 V2 Tower', category: 'Lenovo' },
  // Other Servers
  { value: 'siemens-ipc', label: 'Siemens Industrial PC', category: 'Industrial' },
  { value: 'proxmox-server', label: 'Proxmox Mini Server', category: 'Server' },
  // Storage
  { value: 'odroid-hc4', label: 'ODROID HC4 (Storage)', category: 'Storage' },
  { value: 'synology-ds423', label: 'Synology DS423+', category: 'Storage' },
  { value: 'qnap-ts464', label: 'QNAP TS-464', category: 'Storage' },
  { value: 'truenas-mini', label: 'TrueNAS Mini X+', category: 'Storage' },
  { value: 'nas-server', label: 'NAS Server (Generic)', category: 'Storage' },
  // GPU Nodes
  { value: 'jetson-orin-nano', label: 'Nvidia Jetson Orin Nano', category: 'GPU' },
  { value: 'jetson-agx', label: 'Nvidia Jetson AGX Orin', category: 'GPU' },
  { value: 'gpu-rtx4070', label: 'GPU Workstation (RTX 4070)', category: 'GPU' },
  { value: 'gpu-rtx4090', label: 'GPU Workstation (RTX 4090)', category: 'GPU' },
  { value: 'gpu-l4', label: 'GPU Server (L4)', category: 'GPU' },
  { value: 'gpu-l40', label: 'GPU Server (L40)', category: 'GPU' },
  { value: 'gpu-amd-w7800', label: 'AMD Workstation (PRO W7800)', category: 'GPU' },
  // ARM64 Servers
  { value: 'ampere-altra-mt', label: 'Ampere Altra Mt. Jade (80-core)', category: 'ARM64' },
  { value: 'ampere-altra-max', label: 'Ampere Altra Max (128-core)', category: 'ARM64' },
  { value: 'nvidia-grace-hopper', label: 'NVIDIA Grace Hopper GH200', category: 'ARM64' },
  { value: 'nvidia-grace-cpu', label: 'NVIDIA Grace CPU Superchip', category: 'ARM64' },
  { value: 'marvell-thunderx3', label: 'Marvell ThunderX3 (96-core)', category: 'ARM64' },
  { value: 'adlink-arm-edge', label: 'ADLINK COM-HPC ARM Edge', category: 'ARM64' },
  { value: 'rockchip-rk3588', label: 'Rockchip RK3588 Edge', category: 'ARM64' },
  { value: 'phytium-d2000', label: 'Phytium D2000 (8-core)', category: 'ARM64' },
  // NVIDIA DGX Systems
  { value: 'nvidia-dgx-a100', label: 'NVIDIA DGX A100 (8x A100)', category: 'DGX' },
  { value: 'nvidia-dgx-h100', label: 'NVIDIA DGX H100 (8x H100)', category: 'DGX' },
  { value: 'nvidia-dgx-station', label: 'NVIDIA DGX Station A100', category: 'DGX' },
  { value: 'nvidia-dgx-basepod', label: 'NVIDIA DGX BasePOD (56x H100)', category: 'DGX' },
  { value: 'nvidia-dgx-superpod', label: 'NVIDIA DGX SuperPOD (256x H100)', category: 'DGX' },
  // Custom
  { value: 'custom', label: 'Custom Specs...', category: 'Other' },
];

const NODE_ICONS: Record<NodeType, typeof Server> = {
  gateway: Server,
  compute: Cpu,
  storage: HardDrive,
  daq: Activity,
};

const NODE_COLORS: Record<NodeType, string> = {
  gateway: 'border-l-orange-500',
  compute: 'border-l-blue-500',
  storage: 'border-l-green-500',
  daq: 'border-l-purple-500',
};

export function NodesStep() {
  const {
    config,
    addNode,
    updateNode,
    removeNode,
    duplicateNode,
    reorderNodes,
    // Container actions
    initializeContainer,
    setContainerSize,
    addContainerRack,
    updateContainerRack,
    removeContainerRack,
    moveContainerRack,
    assignNodeToRack,
    unassignNodeFromRack,
    addInfrastructure,
    updateInfrastructure,
    removeInfrastructure,
    moveInfrastructure,
    // Network assignment actions
    assignNodeToNetwork,
    unassignNodeFromNetwork,
    updateNodeNetworkIP,
  } = useConfigurator();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<NodeType[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'rack'>('list');
  const [selectedNodeId, setSelectedNodeId] = useState<string | undefined>();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = config.nodes.findIndex((n) => n.id === active.id);
      const newIndex = config.nodes.findIndex((n) => n.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        reorderNodes(oldIndex, newIndex);
      }
    }
  };

  const gatewayCount = config.nodes.filter((n) => n.type === 'gateway').length;
  const hasGateway = gatewayCount > 0;

  // Filter nodes based on search query and type filter
  const filteredNodes = useMemo(() => {
    return config.nodes.filter((node) => {
      // Type filter
      if (typeFilter.length > 0 && !typeFilter.includes(node.type)) {
        return false;
      }
      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          node.name.toLowerCase().includes(query) ||
          node.hostname.toLowerCase().includes(query) ||
          node.ip.includes(query)
        );
      }
      return true;
    });
  }, [config.nodes, searchQuery, typeFilter]);

  const toggleNodeExpand = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedNodes(new Set(filteredNodes.map((n) => n.id)));
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const allExpanded = filteredNodes.length > 0 && filteredNodes.every((n) => expandedNodes.has(n.id));

  const toggleTypeFilter = (type: NodeType) => {
    setTypeFilter((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter([]);
  };

  const hasActiveFilters = searchQuery !== '' || typeFilter.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Configure Nodes</h2>
        <p className="mt-1 text-gray-500">
          Add and configure the physical nodes in your datacenter. At least one gateway is required.
        </p>
      </div>

      {/* Add Node Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => addNode('gateway')}
          disabled={hasGateway}
          className="gap-2"
        >
          <Server className="h-4 w-4 text-orange-500" />
          Add Gateway
          {hasGateway && <span className="text-xs text-gray-400">(max 1)</span>}
        </Button>
        <Button variant="outline" onClick={() => addNode('compute', undefined, 'worker')} className="gap-2">
          <Cpu className="h-4 w-4 text-blue-500" />
          Add Compute
        </Button>
        <Button variant="outline" onClick={() => addNode('storage')} className="gap-2">
          <HardDrive className="h-4 w-4 text-green-500" />
          Add Storage
        </Button>
        <Button variant="outline" onClick={() => addNode('daq')} className="gap-2">
          <Activity className="h-4 w-4 text-purple-500" />
          Add DAQ
        </Button>
      </div>

      {/* Search and Filter Bar */}
      {config.nodes.length > 3 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2">
          {/* Search Input */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, hostname, or IP..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-8 text-sm focus:border-hermit-500 focus:outline-none focus:ring-1 focus:ring-hermit-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Type Filters */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Filter:</span>
            {(['gateway', 'compute', 'storage', 'daq'] as NodeType[]).map((type) => {
              const Icon = NODE_ICONS[type];
              const isActive = typeFilter.includes(type);
              const count = config.nodes.filter((n) => n.type === type).length;
              return (
                <button
                  key={type}
                  onClick={() => toggleTypeFilter(type)}
                  disabled={count === 0}
                  className={clsx(
                    'flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium transition-colors',
                    isActive
                      ? 'bg-hermit-100 text-hermit-700 ring-1 ring-hermit-300'
                      : count > 0
                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {type} ({count})
                </button>
              );
            })}
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-hermit-600 hover:text-hermit-700"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Filter Results Info */}
      {hasActiveFilters && (
        <div className="text-sm text-gray-500">
          Showing {filteredNodes.length} of {config.nodes.length} nodes
        </div>
      )}

      {/* Warning if no gateway */}
      {!hasGateway && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">
            <strong>Warning:</strong> At least one gateway node is required for the network to function.
          </p>
        </div>
      )}

      {/* Node List Header */}
      {filteredNodes.length > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            {filteredNodes.length} node{filteredNodes.length !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={allExpanded ? collapseAll : expandAll}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            >
              {allExpanded ? (
                <>
                  <ChevronUp className="h-3 w-3" />
                  Collapse all
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" />
                  Expand all
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* View Toggle */}
      {config.nodes.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">View:</span>
          <div className="flex rounded-lg border border-gray-200 p-0.5">
            <button
              onClick={() => setViewMode('list')}
              className={clsx(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                viewMode === 'list'
                  ? 'bg-hermit-100 text-hermit-700'
                  : 'text-gray-600 hover:bg-gray-100'
              )}
            >
              <LayoutList className="h-4 w-4" />
              List
            </button>
            <button
              onClick={() => setViewMode('rack')}
              className={clsx(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                viewMode === 'rack'
                  ? 'bg-hermit-100 text-hermit-700'
                  : 'text-gray-600 hover:bg-gray-100'
              )}
            >
              <LayoutGrid className="h-4 w-4" />
              {config.formFactor.startsWith('rack') ? 'Rack' : 'Layout'}
            </button>
          </div>
        </div>
      )}

      {/* Node List or Rack View */}
      {viewMode === 'list' ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={filteredNodes.map((n) => n.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {filteredNodes.map((node) => (
                <DraggableNodeCard key={node.id} id={node.id}>
                  {(dragHandle) => (
                    <NodeCard
                      node={node}
                      allNodes={config.nodes}
                      expanded={expandedNodes.has(node.id)}
                      onToggleExpand={() => toggleNodeExpand(node.id)}
                      onUpdate={(updates) => updateNode(node.id, updates)}
                      onRemove={() => removeNode(node.id)}
                      onDuplicate={() => duplicateNode(node.id)}
                      canRemove={node.type !== 'gateway' || gatewayCount > 1}
                      formFactor={config.formFactor}
                      dragHandle={dragHandle}
                      segments={config.network.segments.filter(s => s.enabled)}
                      nodeAssignments={config.network.nodeAssignments.filter(a => a.nodeId === node.id)}
                      onAssignToNetwork={(segmentId, ip) => assignNodeToNetwork(node.id, segmentId, ip)}
                      onUnassignFromNetwork={(segmentId) => unassignNodeFromNetwork(node.id, segmentId)}
                      onUpdateNetworkIP={(segmentId, ip) => updateNodeNetworkIP(node.id, segmentId, ip)}
                    />
                  )}
                </DraggableNodeCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : config.formFactor.startsWith('rack') ? (
        <RackView
          formFactor={config.formFactor}
          nodes={config.nodes}
          selectedNodeId={selectedNodeId}
          onNodeClick={(nodeId) => {
            setSelectedNodeId(nodeId);
            setViewMode('list');
            setExpandedNodes(new Set([nodeId]));
          }}
          onReorder={(fromIndex, toIndex) => {
            reorderNodes(fromIndex, toIndex);
          }}
        />
      ) : (
        <ContainerView
          formFactor={config.formFactor}
          nodes={config.nodes}
          containerConfig={config.containerConfig}
          selectedNodeId={selectedNodeId}
          onNodeClick={(nodeId) => {
            setSelectedNodeId(nodeId);
            setViewMode('list');
            setExpandedNodes(new Set([nodeId]));
          }}
          onInitializeContainer={initializeContainer}
          onSetContainerSize={setContainerSize}
          onAddRack={addContainerRack}
          onUpdateRack={updateContainerRack}
          onRemoveRack={removeContainerRack}
          onMoveRack={moveContainerRack}
          onAssignNodeToRack={assignNodeToRack}
          onUnassignNodeFromRack={unassignNodeFromRack}
          onAddInfrastructure={addInfrastructure}
          onUpdateInfrastructure={updateInfrastructure}
          onRemoveInfrastructure={removeInfrastructure}
          onMoveInfrastructure={moveInfrastructure}
        />
      )}

      {config.nodes.length === 0 && (
        <div className="rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
          <Server className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-gray-500">No nodes configured yet</p>
          <p className="text-sm text-gray-400">Add nodes using the buttons above</p>
        </div>
      )}

      {/* Summary */}
      <div className="rounded-lg bg-gray-50 p-4">
        <h3 className="font-medium text-gray-900">Node Summary</h3>
        <div className="mt-2 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
          <div>
            <span className="text-gray-500">Total Nodes:</span>
            <span className="ml-2 font-medium">{config.nodes.length}</span>
          </div>
          <div>
            <span className="text-gray-500">Total Power:</span>
            <span className="ml-2 font-medium">{config.estimatedPower}W</span>
          </div>
          <div>
            <span className="text-gray-500">Est. Cost:</span>
            <span className="ml-2 font-medium">${config.estimatedCost.toFixed(0)}</span>
          </div>
          <div>
            <span className="text-gray-500">Total Storage:</span>
            <span className="ml-2 font-medium">
              {config.nodes.reduce((sum, n) => sum + n.specs.storageGB, 0)}GB
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface NodeCardProps {
  node: DatacenterNode;
  allNodes: DatacenterNode[];
  expanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (updates: Partial<DatacenterNode>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  canRemove: boolean;
  formFactor: import('../../../types/configurator').FormFactor;
  dragHandle?: React.ReactNode;
  // Network assignment props
  segments: NetworkSegment[];
  nodeAssignments: import('../../../types/configurator').NodeNetworkAssignment[];
  onAssignToNetwork: (segmentId: string, ip: string) => void;
  onUnassignFromNetwork: (segmentId: string) => void;
  onUpdateNetworkIP: (segmentId: string, ip: string) => void;
}

function NodeCard({
  node,
  allNodes,
  expanded,
  onToggleExpand,
  onUpdate,
  onRemove,
  onDuplicate,
  canRemove,
  formFactor,
  dragHandle,
  segments,
  nodeAssignments,
  onAssignToNetwork,
  onUnassignFromNetwork,
  onUpdateNetworkIP,
}: NodeCardProps) {
  const Icon = NODE_ICONS[node.type];

  // Validation functions with context
  const validateName = useCallback(
    (value: string) => validateNodeName(value),
    []
  );

  const validateHost = useCallback(
    (value: string) => {
      const hostResult = validateHostname(value);
      if (!hostResult.valid) return hostResult;
      return validateUniqueHostname(
        value,
        allNodes.map((n) => n.hostname),
        node.hostname
      );
    },
    [allNodes, node.hostname]
  );

  const validateIP = useCallback(
    (value: string) => {
      const ipResult = validateIPv4(value);
      if (!ipResult.valid) return ipResult;
      return validateUniqueIP(
        value,
        allNodes.map((n) => n.ip),
        node.ip
      );
    },
    [allNodes, node.ip]
  );

  return (
    <Card className={clsx('border-l-4', NODE_COLORS[node.type])}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {dragHandle}
          <Icon className="h-5 w-5 text-gray-400" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{node.name}</span>
              <NodeTypeBadge type={node.type} />
              {node.role && (
                <Badge variant="info" size="sm">
                  {node.role}
                </Badge>
              )}
            </div>
            <div className="flex gap-3 text-sm text-gray-500">
              <span className="font-mono">{node.ip}</span>
              <span>{node.hostname}.{useConfigurator.getState().config.network.domain}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {node.specs.cpuCores} cores, {node.specs.memoryGB}GB, {node.specs.powerWatts}W
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleExpand}
            className="p-1"
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDuplicate}
            className="p-1"
            title="Duplicate node"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            disabled={!canRemove}
            className="p-1 text-red-500 hover:bg-red-50 hover:text-red-600"
            title={canRemove ? 'Remove node' : 'Cannot remove last gateway'}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {expanded && (
        <CardContent className="mt-4 border-t border-gray-100 pt-4">
          {/* Hardware Preset Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Hardware Model</label>
            <select
              value={
                Object.entries(HARDWARE_CATALOG).find(
                  ([_, hw]) => hw.model === node.specs.model
                )?.[0] || 'custom'
              }
              onChange={(e) => {
                const model = e.target.value as HardwareModel | 'custom';
                if (model !== 'custom' && HARDWARE_CATALOG[model]) {
                  const hw = HARDWARE_CATALOG[model];
                  onUpdate({
                    specs: {
                      cpuCores: hw.cpuCores,
                      memoryGB: hw.memoryGB,
                      storageGB: hw.storageGB,
                      powerWatts: hw.powerWatts,
                      model: hw.model,
                      rackUnits: hw.rackUnits,
                      depthMm: hw.depthMm,
                      gpuSlots: hw.gpuSlots,
                      // Clear GPU if hardware doesn't support it
                      gpu: hw.gpuSlots === 0 ? undefined : hw.gpu,
                    },
                  });
                }
              }}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              {Object.entries(
                HARDWARE_OPTIONS.reduce((acc, opt) => {
                  if (!acc[opt.category]) acc[opt.category] = [];
                  acc[opt.category].push(opt);
                  return acc;
                }, {} as Record<string, typeof HARDWARE_OPTIONS>)
              ).map(([category, options]) => (
                <optgroup key={category} label={category}>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                      {opt.value !== 'custom' && HARDWARE_CATALOG[opt.value as HardwareModel]
                        ? ` ($${HARDWARE_CATALOG[opt.value as HardwareModel].price})`
                        : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {node.specs.model && (
              <p className="mt-1 text-xs text-gray-500">Current: {node.specs.model}</p>
            )}
          </div>

          {/* GPU Selector - show for compute and storage nodes */}
          {(node.type === 'compute' || node.type === 'storage') && (
            <GpuSelector
              selectedGpu={node.specs.gpu}
              formFactor={formFactor}
              gpuSlots={node.specs.gpuSlots}
              onSelect={(gpu: GpuSpecs | undefined) => {
                onUpdate({
                  specs: {
                    ...node.specs,
                    gpu,
                  },
                });
              }}
            />
          )}

          {/* Rack Unit and Panel Color */}
          <div className="mb-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Rack Units (Height)</label>
              <select
                value={node.specs.rackUnits || 1}
                onChange={(e) => {
                  onUpdate({
                    specs: {
                      ...node.specs,
                      rackUnits: parseInt(e.target.value) || 1,
                    },
                  });
                }}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value={1}>1U</option>
                <option value={2}>2U</option>
                <option value={3}>3U</option>
                <option value={4}>4U</option>
                <option value={5}>5U</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">Height in rack units</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Panel Color</label>
              <select
                value={node.specs.panelColor || 'default'}
                onChange={(e) => {
                  onUpdate({
                    specs: {
                      ...node.specs,
                      panelColor: e.target.value === 'default' ? undefined : e.target.value,
                    },
                  });
                }}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="default">Default (by type)</option>
                <option value="#1e3a5f">Navy Blue</option>
                <option value="#2d5016">Forest Green</option>
                <option value="#4a1c1c">Burgundy</option>
                <option value="#1a1a1a">Black</option>
                <option value="#4a4a4a">Silver</option>
                <option value="#3d2914">Brown</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">Visual appearance in rack view</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <ValidatedInput
              label="Name"
              value={node.name}
              onChange={(value) => onUpdate({ name: value })}
              validate={validateName}
              placeholder="e.g., Gateway Node 1"
            />
            <ValidatedInput
              label="Hostname"
              value={node.hostname}
              onChange={(value) => onUpdate({ hostname: value })}
              validate={validateHost}
              placeholder="e.g., gateway-01"
              hint="Letters, numbers, hyphens only"
            />
            <ValidatedInput
              label="IP Address"
              value={node.ip}
              onChange={(value) => onUpdate({ ip: value })}
              validate={validateIP}
              placeholder="e.g., 192.168.200.1"
            />
            {node.type === 'compute' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={node.role || 'worker'}
                  onChange={(e) => onUpdate({ role: e.target.value as ComputeRole })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="controller">Controller</option>
                  <option value="worker">Worker</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            )}
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Input
              label="CPU Cores"
              type="number"
              value={node.specs.cpuCores}
              onChange={(e) =>
                onUpdate({ specs: { ...node.specs, cpuCores: parseInt(e.target.value) || 1 } })
              }
            />
            <Input
              label="Memory (GB)"
              type="number"
              value={node.specs.memoryGB}
              onChange={(e) =>
                onUpdate({ specs: { ...node.specs, memoryGB: parseInt(e.target.value) || 1 } })
              }
            />
            <Input
              label="Storage (GB)"
              type="number"
              value={node.specs.storageGB}
              onChange={(e) =>
                onUpdate({ specs: { ...node.specs, storageGB: parseInt(e.target.value) || 1 } })
              }
            />
            <Input
              label="Power (W)"
              type="number"
              value={node.specs.powerWatts}
              onChange={(e) =>
                onUpdate({ specs: { ...node.specs, powerWatts: parseInt(e.target.value) || 1 } })
              }
            />
          </div>

          {/* Network Segment Assignments */}
          {segments.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Network className="h-4 w-4 text-gray-500" />
                <label className="text-sm font-medium text-gray-700">Network Assignments</label>
              </div>
              <div className="space-y-2">
                {segments.map((segment) => {
                  const assignment = nodeAssignments.find(a => a.segmentId === segment.id);
                  const isAssigned = !!assignment;
                  return (
                    <div
                      key={segment.id}
                      className={clsx(
                        'flex items-center gap-3 rounded-lg border p-2',
                        isAssigned ? 'border-gray-300 bg-gray-50' : 'border-gray-200'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isAssigned}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Generate default IP based on segment subnet
                            const subnetBase = segment.subnet.split('/')[0].split('.').slice(0, 3).join('.');
                            const lastOctet = node.ip.split('.')[3] || '10';
                            const defaultIP = `${subnetBase}.${lastOctet}`;
                            onAssignToNetwork(segment.id, defaultIP);
                          } else {
                            onUnassignFromNetwork(segment.id);
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-hermit-600 focus:ring-hermit-500"
                      />
                      <div
                        className="h-3 w-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: segment.color }}
                        title={segment.name}
                      />
                      <span className="text-sm font-medium text-gray-700 min-w-[100px]">
                        {segment.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({segment.accessLevel})
                      </span>
                      {isAssigned && (
                        <input
                          type="text"
                          value={assignment.ip}
                          onChange={(e) => onUpdateNetworkIP(segment.id, e.target.value)}
                          placeholder="IP Address"
                          className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm font-mono focus:border-hermit-500 focus:outline-none focus:ring-1 focus:ring-hermit-500"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              {segments.length > 0 && nodeAssignments.length === 0 && (
                <p className="mt-2 text-xs text-gray-500">
                  Assign this node to network segments for multi-network deployments.
                </p>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
